import React, { useEffect, useRef, useState } from 'react';
import { aboutData } from '../data/mock';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative min-h-screen bg-black py-24 overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-pink-500/5 to-transparent" />

      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <span className="text-pink-400 text-xs tracking-[0.3em] block mb-4">ABOUT</span>
        <h2 className={`text-white text-3xl md:text-4xl lg:text-5xl font-extralight tracking-wide transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {aboutData.title}
        </h2>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Image */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
            <div className="relative overflow-hidden">
              <img
                src={aboutData.image}
                alt="About Sofia Russo"
                className="w-full h-[700px] object-cover"
              />
              {/* Pink overlay on hover */}
              <div className="absolute inset-0 bg-pink-400/0 hover:bg-pink-400/10 transition-colors duration-500" />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-pink-400/30" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-pink-400/30" />
          </div>

          {/* Text content */}
          <div className="space-y-12">
            {/* Background */}
            <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
              <h3 className="text-white text-xl tracking-[0.1em] mb-4 flex items-center">
                <span className="w-8 h-px bg-pink-400 mr-4" />
                {aboutData.background.title}
              </h3>
              <p className="text-white/60 leading-relaxed whitespace-pre-line">
                {aboutData.background.content}
              </p>
            </div>

            {/* Location */}
            <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
              <h3 className="text-white text-xl tracking-[0.1em] mb-4 flex items-center">
                <span className="w-8 h-px bg-pink-400 mr-4" />
                {aboutData.location.title}
              </h3>
              <p className="text-white/60 leading-relaxed whitespace-pre-line">
                {aboutData.location.content}
              </p>
            </div>

            {/* Passions */}
            <div className={`transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
              <h3 className="text-white text-xl tracking-[0.1em] mb-6 flex items-center">
                <span className="w-8 h-px bg-pink-400 mr-4" />
                {aboutData.passions.title}
              </h3>
              <div className="flex flex-wrap gap-3">
                {aboutData.passions.items.map((passion, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white/5 text-pink-400 text-sm tracking-[0.1em] border border-pink-400/20 hover:bg-pink-400/10 transition-colors duration-300"
                  >
                    {passion.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
};

export default AboutSection;
